import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { QuarterData } from './quarterly-matrix.interface';

@Component({
  selector: 'app-quarterly-matrix',
  templateUrl: './app-quarterly-matrix.component.html',
  styleUrls: ['./app-quarterly-matrix.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AppQuarterlyMatrixComponent {
  /** Quarter 1 dataset (array or single object) */
  @Input() q1Data: QuarterData[] | QuarterData | null = null;

  /** Quarter 2 dataset (array or single object) */
  @Input() q2Data: QuarterData[] | QuarterData | null = null;

  /** Quarter 3 dataset (array or single object) */
  @Input() q3Data: QuarterData[] | QuarterData | null = null;

  /** Quarter 4 dataset (array or single object) */
  @Input() q4Data: QuarterData[] | QuarterData | null = null;

  /** Current active quarter index (1, 2, 3, or 4) */
  @Input() activeQuarter: number = 0;

  /** Specific active override flags */
  @Input() q1Active: boolean = false;
  @Input() q2Active: boolean = false;
  @Input() q3Active: boolean = false;
  @Input() q4Active: boolean = false;

  /** Unit label in subtext (e.g. 'Invoices', 'Quotations', 'Sales Orders') */
  @Input() unitLabel: string = 'Invoices';

  /** Property name for amount in quarter item */
  @Input() amountKey: string = 'amount';

  /** Property name for record count in quarter item */
  @Input() countKey: string = 'invoiceCount';

  /** Optional container class */
  @Input() customClass: string = '';

  /** Emitted when a quarter box is clicked */
  @Output() quarterSelect = new EventEmitter<number>();

  getQuarterItem(quarterNum: number): QuarterData | null {
    let data: any = null;
    switch (quarterNum) {
      case 1:
        data = this.q1Data;
        break;
      case 2:
        data = this.q2Data;
        break;
      case 3:
        data = this.q3Data;
        break;
      case 4:
        data = this.q4Data;
        break;
    }
    if (!data) return null;
    return Array.isArray(data) ? (data[0] || null) : data;
  }

  getQuarterAmount(quarterNum: number): any {
    const item = this.getQuarterItem(quarterNum);
    return item ? (item[this.amountKey] ?? '0.00') : '0.00';
  }

  getQuarterCount(quarterNum: number): any {
    const item = this.getQuarterItem(quarterNum);
    return item ? (item[this.countKey] ?? 0) : 0;
  }

  isQuarterActive(quarterNum: number): boolean {
    const q1 = this.getQuarterItem(1);
    const q2 = this.getQuarterItem(2);
    const q3 = this.getQuarterItem(3);
    const q4 = this.getQuarterItem(4);

    if (quarterNum === 1) {
      return (
        this.q1Active ||
        this.activeQuarter === 1 ||
        (q1?.current == 1 &&
          !this.q2Active &&
          !this.q3Active &&
          !this.q4Active &&
          !this.activeQuarter)
      );
    }
    if (quarterNum === 2) {
      return (
        this.q2Active ||
        this.activeQuarter === 2 ||
        (q2?.current == 2 &&
          !this.q1Active &&
          !this.q3Active &&
          !this.q4Active &&
          !this.activeQuarter)
      );
    }
    if (quarterNum === 3) {
      return (
        this.q3Active ||
        this.activeQuarter === 3 ||
        (q3?.current == 3 &&
          !this.q1Active &&
          !this.q2Active &&
          !this.q4Active &&
          !this.activeQuarter)
      );
    }
    if (quarterNum === 4) {
      return (
        this.q4Active ||
        this.activeQuarter === 4 ||
        (q4?.current == 4 &&
          !this.q1Active &&
          !this.q2Active &&
          !this.q3Active &&
          !this.activeQuarter)
      );
    }
    return false;
  }

  onSelectQuarter(quarterNum: number): void {
    this.quarterSelect.emit(quarterNum);
  }
}
